import { useQueryClient } from '@tanstack/react-query'
import cn from 'classnames'
import AlertPanel from 'ext-ui/components/AlertPanel/AlertPanel'
import ContentCard from 'ext-ui/components/ContentCard/ContentCard'
import AlertTriangleYellowIcon from 'ext-ui/svg/icon/AlertTriangleYellowIcon'
import CheckmarkCircleIcon from 'ext-ui/svg/icon/CheckmarkCircleIcon'
import LinkIcon from 'ext-ui/svg/icon/LinkIcon'
import React, { KeyboardEvent, useState } from 'react'
import { useDispatch } from 'react-redux'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import ConnectPhysicalCardModal from 'src/component-modal/ConnectPhysicalCardModal/ConnectPhysicalCardModal'
import { SupportedBrands } from 'src/enums'
import { useTranslate } from 'src/localization/Trns'
import {
  CreditCardDisplayStatus,
  CreditCardStatus,
  PhysicalCardStatus,
} from 'src/models/creditCard.models'
import { CreditCardListModel } from 'src/models/creditCard.models'
import { creditCardsQueryKeys } from 'src/queries/queries.constants'
import { useWhitelabel } from 'src/utils/whitelabel.utils'

import style from './VirtualCardCapabilitiesPanel.style'

type VirtualCardCapabilitiesPanelProps = {
  className?: string
  creditCard: CreditCardListModel
}

const determineInitialConnectedStatus = (
  creditCard: CreditCardListModel,
): boolean => {
  if (
    creditCard.partner.virtualCardsSupported === true &&
    creditCard.partner.status !== CreditCardStatus.Active
  ) {
    return false
  }
  return Boolean(
    creditCard.partner.virtualCardsSupported &&
      creditCard.partner.status === CreditCardStatus.Active,
  )
}

export default function VirtualCardCapabilitiesPanel(
  props: VirtualCardCapabilitiesPanelProps,
) {
  const { className = '', creditCard } = props
  // State hooks
  // Optimistic-only. The rendered state is DERIVED from the card below, not
  // snapshotted at mount: this component stays mounted while its guard returns
  // null, so a card that arrives here already connected (physical-first
  // enrollment completes, the detail invalidation lands, and the backend has
  // auto-enrolled the virtual side) would otherwise keep the `false` computed
  // on the very first render and offer a live Connect for a capability that is
  // already on. This flag only ever forces "connected" after a local connect
  // succeeds, so it can never regress a fresh server state.
  const [connectedLocally, setConnectedLocally] = useState<boolean>(false)
  const isConnected =
    connectedLocally || determineInitialConnectedStatus(creditCard)
  // TODO: Check this logic and fix when requirements and design are
  // finalized
  const [showDisconnectAlertPanel, _setShowDisconnectAlertPanel] =
    useState<boolean>(false)
  const [showConnectModal, setShowConnectModal] = useState<boolean>(false)
  const [isVerifying, setIsVerifying] = useState<boolean>(false)

  // Custom hooks
  const { whiteLabelIncludes } = useWhitelabel()
  const translate = useTranslate()
  const dispatch = useDispatch()
  const queryClient = useQueryClient()

  const shouldDarkenPanel = whiteLabelIncludes([
    SupportedBrands.Boc,
    SupportedBrands.Pnc,
    SupportedBrands.Bmo,
    SupportedBrands.Corpay,
    SupportedBrands.PacWest,
  ])

  if (
    whiteLabelIncludes(SupportedBrands.Jpm) ||
    creditCard.displayStatus === CreditCardDisplayStatus.Closed
  ) {
    return null
  }

  // For the time being as darkened variants are coming and not in Figma, let's just
  // hide the darkened state and not show the panel at all to keep consistent
  if (shouldDarkenPanel) {
    return null
  }

  // "No physical card yet" has three wire shapes, not two. `ENROLLMENT_AVAILABLE`
  // is the response-only status extend-api returns for a card that is *eligible*
  // for a physical card but has not enrolled one (`CreditCardView`:
  // `physicalCardStatus ?: ENROLLMENT_AVAILABLE.takeIf { physicalCardEnrollable }`).
  // Omitting it here let this panel offer "Enable Virtual Card Capabilities" on a
  // card whose physical enrollment hadn't started — right beside the physical
  // panel's own Connect, which handles the same status correctly. A user who
  // takes the virtual one first puts the account in PENDING with an issuer-side
  // CVS that a later /physical then makes unreachable (EX-40945). For a
  // dual-capability card /physical must go first; the backend auto-enrolls the
  // virtual side once the physical card is ACTIVE.
  // `ENROLLMENT_AVAILABLE` is scoped to "not yet virtual-connected": an upgraded
  // card whose issuer account is already ACTIVE renders no Connect (there is
  // nothing to offer) and must keep its "Enabled" confirmation. `partner.status`,
  // not `creditCard.status` — the issuer verify path only ever writes
  // `account.status`, and the card-level status can sit at PENDING after a
  // successful verification.
  //
  // A null status is deliberately NOT in this set. Null and `ENROLLMENT_AVAILABLE`
  // carry opposite meanings: the API emits `ENROLLMENT_AVAILABLE` only when
  // `physicalCardEnrollable`, so null means the card cannot enroll a physical card
  // at all. The physical-first ordering hazard above can only arise when physical
  // is genuinely on offer, so there is nothing to protect on a null card — and
  // PhysicalCardTransactionsPanel hides itself on a null status too (barring the
  // missing-card-details carve-out). Treating null as "not enrolled yet" therefore
  // hid BOTH panels and left a card whose `/virtual` call never landed with no way
  // to connect anything: stub created, `partner.status` NOT_APPLICABLE, physical
  // status null. That is the wedge this arm exists to end (EX-40945).
  const virtualConnected =
    creditCard.partner?.status === CreditCardStatus.Active
  const physicalCardNotEnrolled =
    creditCard.physicalCard.status === PhysicalCardStatus.NotApplicable ||
    (creditCard.physicalCard.status ===
      PhysicalCardStatus.EnrollmentAvailable &&
      !virtualConnected)

  // `isVirtualAccount` is a company-account (Sunflower) discriminator, not an
  // enrollment signal — elsewhere it only swaps "Account Information" for
  // "Company Information". It stays as a bypass so those accounts always keep the
  // panel, but it must never be the thing that decides an enrollment offer on a
  // normal card; `virtualCardsSupported` is.
  if (
    !creditCard.partner?.virtualCardsSupported ||
    (creditCard.partner.isVirtualAccount === false && physicalCardNotEnrolled)
  ) {
    return null
  }

  const handleConnectClick = () => {
    setShowConnectModal(true)
  }

  return (
    <div
      className={cn('VirtualCardCapabilitiesPanel', className, {
        darkened: shouldDarkenPanel,
        'has-top-alert-panel': showDisconnectAlertPanel,
      })}
      css={style}
    >
      {showDisconnectAlertPanel && (
        <AlertPanel variant="warning">
          <div className="flex-row gap-4 align-top">
            <AlertTriangleYellowIcon />
            <div className="flex-column gap-2">
              <p className="font-bold">
                {translate('Disconnected Integration')}
              </p>
              <p className="font-regular">
                {'PLACEHOLDER TEXT - CHANGE WHEN FINALIZED'}
              </p>
            </div>
          </div>
        </AlertPanel>
      )}
      <ContentCard variant={shouldDarkenPanel ? 'darkened' : 'standard'}>
        <div className="flex-column gap-5">
          <div className="flex-row justify-start gap-2">
            {isConnected && <CheckmarkCircleIcon />}
            <p className="font-h3">
              {translate(
                isConnected
                  ? 'Virtual Card Capabilities Enabled'
                  : 'Enable Virtual Card Capabilities',
              )}
            </p>
          </div>

          <p className="font-regular">
            {translate(
              'Create virtual cards linked to this physical card to control spend, reduce risk, and simplify payments.',
            )}
          </p>
        </div>
      </ContentCard>
      <div className="VirtualCardCapabilitiesPanel-bottom">
        {/* TODO: Remove this condition and render normally when disconnect logic BE is ready */}
        {!isConnected && (
          <>
            <div
              className="linktext-content"
              role="button"
              aria-label={translate('Enable/Disable Virtual Card Capabilities')}
              onClick={() => {
                if (isConnected) {
                  // TODO: disconnect
                } else {
                  handleConnectClick()
                }
              }}
              tabIndex={0}
              onKeyDown={(e: KeyboardEvent) => {
                if (e.key === 'Enter') {
                  if (isConnected) {
                    // TODO: disconnect
                  } else {
                    handleConnectClick()
                  }
                }
              }}
            >
              <div className="flex-row align-center gap-2">
                <p
                  className={cn('font-linktext connect-text', {
                    'disconnect-text': isConnected,
                  })}
                >
                  {translate(isConnected ? 'Disconnect' : 'Connect')}
                </p>
                {!isConnected && <LinkIcon />}
              </div>
            </div>
          </>
        )}
      </div>
      {/* Connect/Disconnect Modals */}

      {/* Connect Modal Here */}
      <ConnectPhysicalCardModal
        isOpen={showConnectModal}
        onClose={() => {
          if (isVerifying) {
            return
          }
          setShowConnectModal(false)
        }}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        reviewEnrollCardProps={{
          onBack: () => {
            setShowConnectModal(false)
          },
          creditCardId: creditCard.id,
          onVerifyStart: () => {
            setIsVerifying(true)
          },
          onVerifyComplete: () => {
            setIsVerifying(false)
            setShowConnectModal(false)

            // TODO: Edit toast message for virtual
            dispatch(
              createSetIsToastOpen(true, {
                toastMessage: (
                  <p>
                    {translate(
                      'Success! Virtual card capabilities are now enabled.',
                    )}
                  </p>
                ),
              }),
            )
            setConnectedLocally(true)

            // Invalidate credit card queries so the Account page
            // refetches and shows the newly available tabs
            queryClient.invalidateQueries({
              queryKey: creditCardsQueryKeys.detail(creditCard.id),
            })
          },
          onVerifyError: () => {
            setIsVerifying(false)
            // TODO: Add error handling here when we get a more robust backend
          },
          submissionData: {
            binData: {
              id: creditCard.issuerId,
              // This is specifically for the VIRTUAL_ONLY Flow for now
              eligibility: 'VIRTUAL_ONLY',
              issuerLogoImg: creditCard.cardImage.urls.small,
              issuerRequiresVerification: false,
            },
            addressData: {
              cardName: creditCard.displayName,
              ...creditCard.address,
            },
            cardData: {
              // We only have access to the last 4 and not the CVC in this view
              // So I think this might be our best bet in getting this to work
              // The component itself already masks the card number minus the last
              // 4 digits so this should be alright
              number: `**** **** **** ${creditCard.last4}`,
              // We are unable to get CVC info from the card detail view since
              // this data lives in VGS and it may be a nasty security issue
              // to display it here if a user's account is compromised
              cvc: '***',
              expiration: creditCard.expiresFormattedString,
            },
          },
        }}
      />
    </div>
  )
}
